import type * as amplitude from '@amplitude/analytics-browser';
import type { Config } from 'app/config';
import type React from 'react';
import { createContext, type ReactNode, useContext, useEffect, useState } from 'react';
import { init } from './amplitude/client';
import { initRecordRelay } from 'app/utils/analytics/recordrelay/client';
import { useAppState } from 'state';

type AmplitudeType = typeof amplitude;

interface Analytics {
  amplitude: AmplitudeType | null;
}

const AnalyticsContext = createContext<Analytics | null>(null);

interface AnalyticsProviderProps {
  children: ReactNode;
  config: Config;
}

export const AnalyticsProvider: React.FC<AnalyticsProviderProps> = ({ children, config }) => {
  const [amplitudeInstance, setAmplitudeInstance] = useState<AmplitudeType | null>(null);
  const { hasConsentBeenSet, consentType } = useAppState(({ cookieConsent }) => cookieConsent);
  const userId = useAppState(({ auth }) => auth.userId) as number | null;

  useEffect(() => {
    // Only initialize analytics if user has given consent for all cookies
    if (hasConsentBeenSet && consentType === 'all' && config.REACT_APP_AMPLITUDE_API_KEY) {
      const instance = init(config.REACT_APP_AMPLITUDE_API_KEY);
      setAmplitudeInstance(instance);
    } else if (hasConsentBeenSet && consentType === 'essential') {
      // User declined analytics - don't initialize
      setAmplitudeInstance(null);
    }
    // If consent hasn't been set yet, don't initialize (wait for user choice)
  }, [hasConsentBeenSet, consentType, config.REACT_APP_AMPLITUDE_API_KEY]);

  useEffect(() => {
    if (
      hasConsentBeenSet &&
      consentType === 'all' &&
      userId &&
      config.REACT_APP_RECORD_RELAY_URL &&
      config.REACT_APP_RECORD_RELAY_API_KEY
    ) {
      initRecordRelay({
        baseUrl: config.REACT_APP_RECORD_RELAY_URL,
        apiKey: config.REACT_APP_RECORD_RELAY_API_KEY,
        userId: userId.toString(),
      });
    }
  }, [
    hasConsentBeenSet,
    consentType,
    userId,
    config.REACT_APP_RECORD_RELAY_URL,
    config.REACT_APP_RECORD_RELAY_API_KEY,
  ]);

  return <AnalyticsContext.Provider value={{ amplitude: amplitudeInstance }}>{children}</AnalyticsContext.Provider>;
};

export const useAnalytics = (): Analytics | null => useContext(AnalyticsContext);
